import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import {
  getAccountSearchPath,
  MINIMUM_TERM_LENGTH
} from '@signal/core/accountSearch/fetchHooks'
import { encodeGuidsToString } from '@signal/core/accountSearch/utils'
import { AccountSearchResultType } from '@signal/core/accountSearch/types'
import { fetchData } from '@signal/core/swr'

export interface ScrollState {
  isAtTop: boolean
  isAtBottom: boolean
  canScroll: boolean
}

export interface UseScrollTrackingReturn {
  scrollRef: React.RefObject<HTMLDivElement>
  scrollState: ScrollState
  scrollToTop: () => void
  scrollToBottom: () => void
}

/**
 * Custom hook for tracking scroll position and providing scroll navigation
 * @param dependencies - Array of dependencies that should trigger a scroll position recheck
 * @returns Object containing scroll ref, state, and navigation functions
 */
export function useScrollTracking(
  dependencies: unknown[] = []
): UseScrollTrackingReturn {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState<ScrollState>({
    isAtTop: true,
    isAtBottom: false,
    canScroll: false
  })

  // Scroll position tracking
  const checkScrollPosition = useCallback(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const { scrollTop, scrollHeight, clientHeight } = scrollElement
    const canScroll = scrollHeight > clientHeight + 1 // Small buffer for precision
    const isAtTop = scrollTop <= 8 // Generous threshold for top
    const isAtBottom = scrollTop >= scrollHeight - clientHeight - 15 // Generous threshold for bottom

    // Only update state if something actually changed
    setScrollState(prevState => {
      if (
        prevState.canScroll === canScroll &&
        prevState.isAtTop === isAtTop &&
        prevState.isAtBottom === isAtBottom
      ) {
        return prevState // No change, don't trigger re-render
      }

      return {
        isAtTop,
        isAtBottom,
        canScroll
      }
    })
  }, [])

  // Scroll handlers
  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const scrollToBottom = useCallback(() => {
    const scrollElement = scrollRef.current
    if (scrollElement) {
      scrollElement.scrollTo({
        top: scrollElement.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [])

  // Set up scroll listener and initial check
  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    let animationFrameId: number | null = null

    const handleScroll = () => {
      // Only schedule one animation frame at a time
      if (animationFrameId) return

      animationFrameId = requestAnimationFrame(() => {
        checkScrollPosition()
        animationFrameId = null
      })
    }

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })

    // Initial check
    const initialCheckTimeout = setTimeout(checkScrollPosition, 100)

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
      clearTimeout(initialCheckTimeout)
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [checkScrollPosition])

  // Recheck scroll position when dependencies change
  // Using a ref to avoid including checkScrollPosition in deps
  const checkScrollPositionRef = useRef(checkScrollPosition)
  checkScrollPositionRef.current = checkScrollPosition

  useEffect(() => {
    // Use a single timeout strategy to avoid conflicts
    const timeoutId = setTimeout(() => checkScrollPositionRef.current(), 100)

    return () => {
      clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies) // Only depend on external dependencies, not checkScrollPosition

  return {
    scrollRef,
    scrollState,
    scrollToTop,
    scrollToBottom
  }
}

export interface UseMenuAccountSearchReturn {
  results: Array<AccountSearchResultType>
  isSearching: boolean
}

/**
 * Searches accounts for the user menu via the accounts search endpoint.
 *
 * This deliberately uses the low-level `fetchData` helper with local state
 * instead of the shared `useAccountSearch` SWR hook. The menu lives in the
 * always-mounted header, and
 * routing the search through the global SWR cache made every keystroke disturb
 * the page's own data (the page content flashed its loading skeleton). Keeping
 * the search isolated means it can never touch the rest of the app's state.
 *
 * @param searchableAccountIds - the account ids the user can access, handed to
 *   the endpoint so it can match the term against name, account id, dealer id
 *   and website
 * @param term - the (already debounced) search term
 * @returns the matching accounts and whether a request is in flight
 */
export function useMenuAccountSearch(
  searchableAccountIds: Array<string>,
  term: string
): UseMenuAccountSearchReturn {
  const [results, setResults] = useState<Array<AccountSearchResultType>>([])
  const [isSearching, setIsSearching] = useState(false)

  const body = useMemo(
    () => JSON.stringify(encodeGuidsToString(searchableAccountIds)),
    [searchableAccountIds]
  )

  useEffect(() => {
    if (term.length < MINIMUM_TERM_LENGTH) {
      setResults([])
      setIsSearching(false)
      return
    }

    let active = true
    setIsSearching(true)

    fetchData({
      url: getAccountSearchPath(term),
      options: { method: 'POST', body }
    })
      .then(data => {
        if (!active) return
        setResults(Array.isArray(data) ? data : data ? [data] : [])
        setIsSearching(false)
      })
      .catch(() => {
        if (!active) return
        setResults([])
        setIsSearching(false)
      })

    return () => {
      active = false
    }
  }, [term, body])

  return { results, isSearching }
}
